import { AxiosResponse } from 'axios'
import {
  InfiniteData,
  Query,
  QueryClient,
  QueryKey,
  queryOptions,
} from '@tanstack/react-query'
import { InferType } from 'yup'
import { NSocieties } from '../interfaces/services/societies.interfaces'
import {
  RegisterUpdatedAction,
  RegisterUpdatedPayload,
} from '../generated/api/live-events'
import { PostRow, PostsPage } from '../generated/api/post-row'
import { CommentsSurface } from '../generated/api/comment-row'
import { createUrl } from './helpers'
import axiosAuth from './config'
import { chatMemberCandidatesQueryKey, chatRecipientsQueryKey } from './chat'
import {
  postFeedQueryKey,
  postPinnedQueryKey,
  postRowEntityKey,
  postScheduledQueryKey,
} from './posts'
import { societyGuidesQueries } from './society-guides'
import { societyUserUnitUpdateSchema } from '../forms/schemas/society_user_unit_update'
import { ISocietiesModel } from '../interfaces/models/societies.interfaces'
import { IUnitModel, getUnitRole } from '../interfaces/models/units.interfaces'
import { sortStringsAlphabetically } from '../helpers/sorting'

export const getUserSocietiesNoPop = (
  userId: string
): Promise<AxiosResponse<NSocieties.NGetUserSocieties.IResponse>> => {
  return axiosAuth.get<NSocieties.NGetUserSocieties.IResponse>(
    createUrl(`/societies/get-user-societies/${userId}`)
  )
}

export const getUserSocieties = (
  userId: string
): Promise<AxiosResponse<NSocieties.NGetUserSocieties.IResponse>> => {
  return axiosAuth.get<NSocieties.NGetUserSocieties.IResponse>(
    createUrl(`/societies/get-user-societies/${userId}`)
  )
}

export const createSociety = (
  body: NSocieties.NCreate.IRequestBody
): Promise<AxiosResponse<NSocieties.NCreate.IResponse>> => {
  return axiosAuth.post<NSocieties.NCreate.IResponse>(
    createUrl('/societies/society'),
    body
  )
}

export const getSociety = (
  id: string
): Promise<AxiosResponse<NSocieties.NGetById.IResponse>> => {
  return axiosAuth.get<NSocieties.NGetById.IResponse>(
    createUrl(`/societies/society/${id}`)
  )
}

export const getSocietyIdUsers = async (
  societyId: string
): Promise<AxiosResponse<NSocieties.NGetSocietyIdUsers.IResponse>> => {
  return axiosAuth.get<NSocieties.NGetSocietyIdUsers.IResponse>(
    createUrl(`/societies/society/${societyId}/users`)
  )
}

export const getSocietyUsers = (): Promise<
  AxiosResponse<NSocieties.NGetSocietyUsers.IResponse>
> => {
  return axiosAuth.get<NSocieties.NGetSocietyUsers.IResponse>(
    createUrl(`/societies/society/users`)
  )
}

export const patchSociety = (params: {
  id: string
  body: NSocieties.NPatch.IRequestBody
}): Promise<AxiosResponse<NSocieties.NPatch.IResponse>> => {
  return axiosAuth.patch<NSocieties.NPatch.IResponse>(
    createUrl(`/societies/society/${params.id}`),
    params.body
  )
}

export const searchSocieties = (
  term: string,
  limit = 50
): Promise<AxiosResponse<NSocieties.NGetUserSocieties.IResponse>> => {
  return axiosAuth.get<NSocieties.NGetUserSocieties.IResponse>(
    createUrl(`/societies/search`, {
      term,
      limit,
    })
  )
}

export const listSocieties = (
  params: NSocieties.NList.IRequestQuery
): Promise<AxiosResponse<NSocieties.NList.IResponse>> => {
  return axiosAuth.get<NSocieties.NList.IResponse>(
    createUrl(`/societies/list`, {
      ...params,
    })
  )
}

export const addAdminUsers = (variables: {
  id: string
  userIds: string[]
}): Promise<AxiosResponse<NSocieties.NUpdateAdminUsers.IResponse>> => {
  const body: NSocieties.NUpdateAdminUsers.IRequestBody = variables.userIds.map(
    (userId) => ({
      userId,
    })
  )
  return axiosAuth.post<NSocieties.NUpdateAdminUsers.IResponse>(
    createUrl(`/societies/update-admin-users/${variables.id}`),
    body
  )
}

export const removeAdminUser = (params: {
  id: string
  userId: string
}): Promise<AxiosResponse<NSocieties.NRemoveAdminUsers.IResponse>> => {
  const body: NSocieties.NRemoveAdminUsers.IRequestBody = [
    { userId: params.userId },
  ]
  return axiosAuth.post<NSocieties.NRemoveAdminUsers.IResponse>(
    createUrl(`/societies/remove-admin-users/${params.id}`),
    body
  )
}

export const addBlockedUsers = (
  id: string,
  userIds: string[]
): Promise<AxiosResponse<NSocieties.NCreateBlockListUsers.IResponse>> => {
  const body: NSocieties.NCreateBlockListUsers.IRequestBody = userIds.map(
    (userId) => ({
      userId,
    })
  )
  return axiosAuth.post<NSocieties.NCreateBlockListUsers.IResponse>(
    createUrl(`/societies/create-block-list-users/${id}`),
    body
  )
}

export const removeBlockedUser = (
  id: string,
  userId: string
): Promise<AxiosResponse<NSocieties.NDeleteBlockListUser.IResponse>> => {
  const body: NSocieties.NDeleteBlockListUser.IRequestBody = { userId }
  return axiosAuth.post<NSocieties.NDeleteBlockListUser.IResponse>(
    createUrl(`/societies/delete-block-list-user/${id}`),
    body
  )
}

export const inviteMembers = (
  id: string,
  users: { userEmail: string }[]
): Promise<AxiosResponse<NSocieties.NAddUsersRoles.IResponse>> => {
  const body: NSocieties.NAddUsersRoles.IRequestBody = users.map((user) => ({
    societyId: id,
    userEmail: user.userEmail,
  }))
  return axiosAuth.post<NSocieties.NAddUsersRoles.IResponse>(
    createUrl(`/societies/users-roles`, { sendNotifications: true }),
    body
  )
}

export const createUserRole = (variables: {
  body: NSocieties.NAddUsersRoles.IRequestBody
  sendNotifications: boolean
}): Promise<AxiosResponse<NSocieties.NAddUsersRoles.IResponse>> => {
  return axiosAuth.post<NSocieties.NAddUsersRoles.IResponse>(
    createUrl(`/societies/users-roles`, {
      sendNotifications: !!variables.sendNotifications,
    }),
    variables.body
  )
}

export const deleteUserRole = (
  body: NSocieties.NRemoveUsersRoles.IRequestBody
): Promise<AxiosResponse<NSocieties.NRemoveUsersRoles.IResponse>> => {
  return axiosAuth.delete<NSocieties.NRemoveUsersRoles.IResponse>(
    createUrl(`/societies/users-roles`),
    {
      data: body,
    }
  )
}

export const removeUserFromSociety = (variables: {
  userId: string
  societyId: string
}): Promise<AxiosResponse<NSocieties.NRemoveUsersRoles.IResponse>> => {
  return deleteUserRole([
    {
      userId: variables.userId,
      societyId: variables.societyId,
      removeFromSociety: true,
      unitRole: true,
      boardRole: true,
      adminRole: true,
      nominatingCommitteeRole: true,
    },
  ])
}

export const removeSelfFromSociety = (params: {
  societyId: string
}): Promise<AxiosResponse<NSocieties.NRemoveSelfFromSociety.IResponse>> => {
  return axiosAuth.get<NSocieties.NRemoveSelfFromSociety.IResponse>(
    createUrl(`/societies/remove-self-from-society/${params.societyId}`)
  )
}

export const removePreApprovedUser = (variables: {
  id: string
  body: NSocieties.NRemovePreApprovedUser.IRequestBody
}): Promise<AxiosResponse<NSocieties.NRemovePreApprovedUser.IResponse>> => {
  return axiosAuth.post<NSocieties.NRemovePreApprovedUser.IResponse>(
    createUrl(`/societies/remove-pre-approved-users/${variables.id}`),
    variables.body
  )
}

export const sendPreApprovedUserInviteReminder = (variables: {
  id: string
  email: string
}): Promise<AxiosResponse<NSocieties.NInvitePreApproved.IResponse>> => {
  const body: NSocieties.NInvitePreApproved.IRequestBody = {
    isInviteAll: false,
    isInviteHousehold: false,
    preApprovedUserEmails: [variables.email],
  }
  return axiosAuth.post<NSocieties.NAddUsersRoles.IResponse>(
    createUrl(`/societies/invite-pre-approved/${variables.id}`),
    body
  )
}

export const inviteAllPreApprovedUsers = (
  id: string
): Promise<AxiosResponse<NSocieties.NInvitePreApproved.IResponse>> => {
  const body: NSocieties.NInvitePreApproved.IRequestBody = {
    isInviteAll: true,
    isInviteHousehold: false,
    preApprovedUserEmails: [],
  }
  return axiosAuth.post<NSocieties.NAddUsersRoles.IResponse>(
    createUrl(`/societies/invite-pre-approved/${id}`),
    body
  )
}

export const activateSociety = (
  id: string
): Promise<AxiosResponse<NSocieties.NActivateSociety.IResponse>> => {
  return axiosAuth.post<NSocieties.NActivateSociety.IResponse>(
    createUrl(`/societies/activate-society/${id}`)
  )
}

export const getSocietyListings = (
  id: string,
  limit = 100,
  offset = 0
): Promise<AxiosResponse<NSocieties.NListings.IResponse>> => {
  return axiosAuth.get<NSocieties.NListings.IResponse>(
    createUrl(`/societies/society/${id}/listings`, { limit, offset })
  )
}

export const getSocietySold = (
  id: string,
  limit = 100,
  offset = 0
): Promise<AxiosResponse<NSocieties.NSold.IResponse>> => {
  return axiosAuth.get<NSocieties.NSold.IResponse>(
    createUrl(`/societies/society/${id}/sold`, { limit, offset })
  )
}

export const downloadMemberRegister = async (
  id: string,
  onlyResidents?: boolean
): Promise<AxiosResponse<NSocieties.NDownloadUsers.IResponse>> => {
  const societyResidentsEndpoint = createUrl(`/societies/download/${id}`, {
    onlyResidents,
  })
  return axiosAuth.post(createUrl(`/users/ephemeral-endpoint`), {
    endpoint: societyResidentsEndpoint,
    maxTimeMS: 1000 * 60 * 30,
  })
}

export const downloadApartmentRegister = async (
  id: string
): Promise<AxiosResponse<NSocieties.NDownloadApartments.IResponse>> => {
  const societyApartmentsEndpoint = createUrl(
    `/societies/download-apartments/${id}`
  )
  return axiosAuth.post(createUrl(`/users/ephemeral-endpoint`), {
    endpoint: societyApartmentsEndpoint,
    maxTimeMS: 1000 * 60 * 30,
  })
}

export const uploadMemberRegister = (variables: {
  id: string
  body: NSocieties.NUploadUsers.IRequestBody
}): Promise<AxiosResponse<NSocieties.NUploadUsers.IResponse>> => {
  return axiosAuth.post<NSocieties.NUploadUsers.IResponse>(
    createUrl(`/societies/upload/${variables.id}`),
    variables.body
  )
}

export const uploadApartmentRegister = (variables: {
  id: string
  body: NSocieties.NUploadApartments.IRequestBody
}): Promise<AxiosResponse<NSocieties.NUploadApartments.IResponse>> => {
  return axiosAuth.post<NSocieties.NUploadApartments.IResponse>(
    createUrl(`/societies/upload-apartments/${variables.id}`),
    variables.body
  )
}

export const getSocietyPremiumCost = (params: {
  id: string
  query: NSocieties.NCostSummary.IRequestQuery
}): Promise<AxiosResponse<NSocieties.NCostSummary.IResponse>> => {
  return axiosAuth.get<NSocieties.NCostSummary.IResponse>(
    createUrl(`/societies/society/${params.id}/cost-summary`, params.query)
  )
}

export const startPremium = (
  id: string,
  discountCode?: string
): Promise<AxiosResponse<NSocieties.NStartPremium.IResponse>> => {
  const body: NSocieties.NPremiumInformation.IRequestBody = { discountCode }

  return axiosAuth.post<NSocieties.NStartPremium.IResponse>(
    createUrl(`/societies/society/${id}/start-premium`),
    body
  )
}

export const getSocietyPremiumInformation = async (
  id: string,
  discountCode?: string
): Promise<AxiosResponse<NSocieties.NPremiumInformation.IResponse>> => {
  const body: NSocieties.NPremiumInformation.IRequestBody = { discountCode }

  const r = await axiosAuth.post<NSocieties.NPremiumInformation.IResponse>(
    createUrl(`/societies/society/${id}/premium-information`),
    body
  )
  return r
}

export const getUserBlockedSocietyIds = (): Promise<
  AxiosResponse<NSocieties.NGetUserBlockedSocietyIds.IResponse>
> => {
  return axiosAuth.get<NSocieties.NGetUserBlockedSocietyIds.IResponse>(
    createUrl('/societies/get-user-blocked-society-ids')
  )
}

export const updateUserUnits = async (
  formEntrances: InferType<typeof societyUserUnitUpdateSchema>[],
  existingEntrances: InferType<typeof societyUserUnitUpdateSchema>[]
): Promise<void> => {
  // All new entrances do not have a _id
  const newEntrances = formEntrances.filter((entrance) => !entrance._id)

  // Entrances that have been updated matches on _id
  // but objects do not match
  const updatedEntrances = formEntrances.filter((formEntrance) =>
    existingEntrances.find(
      (existingEntrance) =>
        existingEntrance._id === formEntrance._id &&
        JSON.stringify(existingEntrance) !== JSON.stringify(formEntrance)
    )
  )

  // Existing entrance objects that have been updated exist in updatedEntrances
  // and matches on _id
  const existingEntrancesThatHaveBeenUpdated = existingEntrances.filter(
    (existingEntrance) =>
      updatedEntrances
        .map((updatedEntrance) => updatedEntrance._id)
        .includes(existingEntrance._id)
  )

  // Since existing entrances all have an _id, the deleted entrances
  // are the entrances that are exist in existingEntrances but are missing from formEntrances
  const deletedEntrances = existingEntrances.filter(
    (existingEntrance) =>
      formEntrances
        .map((_entrance) => _entrance._id)
        .indexOf(existingEntrance._id) === -1
  )

  const userRolesToCreate = [...updatedEntrances, ...newEntrances]
  const userRolesToDelete = [
    ...deletedEntrances,
    ...existingEntrancesThatHaveBeenUpdated,
  ]

  // @ts-expect-error legacy typings TODO: Fix error
  await deleteUserRole(userRolesToDelete)
  await createUserRole({
    sendNotifications: false,
    body: userRolesToCreate.map((userRole) => {
      return {
        societyId: userRole.societyId,
        _id: userRole._id,
        userId: userRole.userId,
        unitRole: getUnitRole(userRole.unitRole),
        entranceId: userRole.entranceId,
        unitIdOrTitle: userRole.unitIdOrTitle,
      }
    }),
  })
}

export const societyQueries = {
  getUserSocieties: (userId?: string) =>
    queryOptions({
      queryKey: [{ scope: 'society', entity: 'list', userId }],
      queryFn: async ({ queryKey }) => {
        const societies = (await getUserSocieties(queryKey[0].userId!)).data
          .data
        societies.sort((a, b) => sortStringsAlphabetically(a.name, b.name))
        return societies
      },
      enabled: !!userId,
    }),
  getUserPreaprovedSocieties: ({ userEmail }: { userEmail?: string }) =>
    queryOptions({
      queryKey: [{ scope: 'society', entity: 'preApproved', userEmail }],
      queryFn: async ({ queryKey }) => {
        return (
          await listSocieties({
            isSearchByPreApproved: true,
            isSearchByOrgNumber: false,
            isSearchByName: false,
            limit: 10,
            term: queryKey[0].userEmail!,
          })
        ).data.data
      },
      enabled: !!userEmail,
    }),

  getSocietyIdUsers: (societyId?: string) =>
    queryOptions({
      queryKey: [{ scope: 'society', entity: 'getSocietyIdUsers', societyId }],
      enabled: !!societyId,
      queryFn: async ({ queryKey }) =>
        (await getSocietyIdUsers(queryKey[0].societyId!)).data?.data?.sort(
          (a, b) => sortStringsAlphabetically(a.fullName, b.fullName)
        ),
    }),
  getSocietyUsers: () =>
    queryOptions({
      queryKey: [{ scope: 'society', entity: 'getSocietyUsers' }],
      queryFn: async () =>
        (await getSocietyUsers()).data.data?.sort((a, b) =>
          sortStringsAlphabetically(a.fullName, b.fullName)
        ),
    }),
  getSociety: (societyId?: string) =>
    queryOptions({
      queryKey: [{ scope: 'society', entity: 'detail', societyId }],
      queryFn: async ({ queryKey }) => {
        return (await getSociety(queryKey[0].societyId!)).data.data
      },
      enabled: !!societyId,
    }),
  getSocietiesByOrganisationNumber: (organisationNumber?: string) =>
    queryOptions({
      queryKey: [{ scope: 'society', entity: 'list', organisationNumber }],
      queryFn: async ({ queryKey }) => {
        const orgNumber = queryKey[0].organisationNumber?.replace(
          /(\d{6})(\d{4})/,
          '$1-$2'
        )
        return (
          await listSocieties({
            isSearchByOrgNumber: true,
            term: orgNumber!,
          })
        ).data.data
      },
      enabled: !!organisationNumber && organisationNumber.trim().length === 10,
    }),
  getUserBlockedSocietyIds: () =>
    queryOptions({
      queryKey: [{ scope: 'society', entity: 'userBlockedSocietyIds' }],
      queryFn: async () => (await getUserBlockedSocietyIds()).data.data,
    }),
  preApprovedList: (email?: string) =>
    queryOptions({
      queryKey: [
        {
          scope: 'preAproved',
          entity: 'list',
          params: {
            isSearchByPreApproved: true,
            isSearchByOrgNumber: false,
            isSearchByName: false,
            limit: 10,
            term: email,
          },
        },
      ],
      enabled: !!email,
      queryFn: async ({ queryKey }) => {
        const { term } = queryKey[0].params
        if (!term) return []
        return (await listSocieties({ ...queryKey[0].params, term })).data.data
      },
    }),
  getSocietyPremiumCost: (societyId?: string, partnerNameId?: string) =>
    queryOptions({
      queryKey: [
        { scope: 'society', entity: 'premiumCost', societyId, partnerNameId },
      ],
      queryFn: async ({ queryKey }) => {
        return (
          await getSocietyPremiumCost({
            id: queryKey[0].societyId!,
            query: { partnerNameId: queryKey[0].partnerNameId },
          })
        ).data.data
      },
      enabled: !!societyId,
    }),

  getSocietyPremiumInformation: (societyId?: string, discountCode?: string) =>
    queryOptions({
      queryKey: [
        {
          scope: 'society',
          entity: 'premiumInformation',
          societyId,
          discountCode,
        },
      ],
      queryFn: async ({ queryKey }) => {
        return (
          await getSocietyPremiumInformation(
            queryKey[0].societyId!,
            queryKey[0].discountCode
          )
        ).data.data
      },
      enabled: !!societyId,
    }),
  searchSocieties: (term: string) =>
    queryOptions({
      queryKey: [{ scope: 'society', entity: 'search', term }],
      enabled: !!term,
      queryFn: async ({ queryKey }) => {
        return (await searchSocieties(queryKey[0].term)).data.data
      },
    }),
}

const registerKeys = (
  viewerId: string | undefined
): Record<RegisterUpdatedAction, QueryKey[]> => {
  const units = [{ scope: 'unit' }]
  const detail = [{ scope: 'society', entity: 'detail' }]
  const userSocieties = societyQueries.getUserSocieties(viewerId).queryKey
  const societyIdUsers = [{ scope: 'society', entity: 'getSocietyIdUsers' }]
  const societyUsers = societyQueries.getSocietyUsers().queryKey
  const chatSearches = [chatRecipientsQueryKey, chatMemberCandidatesQueryKey]
  return {
    units: [units, societyIdUsers, societyUsers, ...chatSearches],
    entrances: [[{ scope: 'entrance' }], units, societyIdUsers],
    roles: [
      detail,
      userSocieties,
      societyIdUsers,
      societyUsers,
      ...chatSearches,
    ],
    'block-list': [
      detail,
      userSocieties,
      ...chatSearches,
      [{ scope: 'chat', entity: 'room' }],
    ],
    requests: [[{ scope: 'requests', entity: 'society' }]],
    'pre-approved': [detail],
  }
}

const notOtherSociety =
  (societyId: string) =>
  ({ queryKey, state }: Query): boolean => {
    const [{ societyId: keySociety }] = queryKey as [
      { societyId?: string | null },
    ]
    const dataSociety = (state.data as { societyId?: string } | null)?.societyId
    return [keySociety, dataSociety].every(
      (id) => id == null || id === societyId
    )
  }

const invitesHolding = (client: QueryClient, societyId: string): QueryKey[] =>
  client
    .getQueriesData<ISocietiesModel[]>({
      queryKey: [{ scope: 'society', entity: 'preApproved' }],
    })
    .filter(([, societies]) => societies?.some(({ _id }) => _id === societyId))
    .map(([queryKey]) => queryKey)

export const applyRegisterUpdated = async (
  client: QueryClient,
  { societyId, action }: RegisterUpdatedPayload,
  viewerId: string | undefined
): Promise<void> => {
  const queryKeys = [
    // An action added to the api later finds no keys.
    ...(registerKeys(viewerId)[action] ?? []),
    ...(action === 'units' || action === 'entrances'
      ? invitesHolding(client, societyId)
      : []),
  ]
  await Promise.all(
    queryKeys.map((queryKey) =>
      client.invalidateQueries({
        queryKey,
        predicate: notOtherSociety(societyId),
      })
    )
  )
}

export const applyMembershipUpdated = (client: QueryClient): Promise<void> =>
  client.invalidateQueries()

const holds =
  <T>(test: (data: T) => boolean) =>
  ({ state }: Query): boolean =>
    state.data != null && test(state.data as T)

export const applySocietySettingsUpdated = async (
  client: QueryClient,
  { societyId }: { societyId: string },
  viewerId: string | undefined
): Promise<void> => {
  const ofSociety = ({ society }: { society: { id: string } }): boolean =>
    society.id === societyId
  const listHoldsSociety = holds<PostRow[]>((rows) => rows.some(ofSociety))
  const queryKeys: QueryKey[] = [
    societyQueries.getUserSocieties(viewerId).queryKey,
    societyQueries.getSociety(societyId).queryKey,
    [{ scope: 'society', entity: 'premiumInformation', societyId }],
    societyGuidesQueries.gettingStarted(societyId).queryKey,
    ...invitesHolding(client, societyId),
  ]
  await Promise.all([
    ...queryKeys.map((queryKey) => client.invalidateQueries({ queryKey })),
    client.invalidateQueries({
      queryKey: [{ scope: 'chat' }],
      predicate: notOtherSociety(societyId),
    }),
    // Comment permissions on rows and discussions follow the settings.
    client.invalidateQueries({
      queryKey: postFeedQueryKey,
      predicate: holds<InfiniteData<PostsPage>>(({ pages }) =>
        pages.some(({ items }) => items.some(ofSociety))
      ),
    }),
    ...[postPinnedQueryKey, postScheduledQueryKey].map((queryKey) =>
      client.invalidateQueries({ queryKey, predicate: listHoldsSociety })
    ),
    client.invalidateQueries({
      queryKey: postRowEntityKey,
      predicate: holds<PostRow>(ofSociety),
    }),
    client.invalidateQueries({
      queryKey: [{ scope: 'comments', entity: 'discussion' }],
      predicate: holds<InfiniteData<CommentsSurface>>(({ pages }) =>
        pages.some(ofSociety)
      ),
    }),
  ])
}

export function canRemoveUserFromSociety(
  userId: string,
  society: ISocietiesModel,
  societyUnits?: IUnitModel[]
): boolean {
  if (!society) {
    return false
  }

  if (!society.masterAdminUserId) {
    return true
  }

  const residentUsers =
    societyUnits?.flatMap((unit) => unit.residentsList) || []

  // Safeguard for a problem where the backend did not return this property on the object
  // (meaning we could send delete request for the master admin). Better disallow removal in this case
  if (!Object.prototype.hasOwnProperty.call(society, 'masterAdminUserId')) {
    return false
  }

  if (society.masterAdminUserId === userId && residentUsers.length > 1) {
    // If the user is master admin and there are other users in the society
    // we cannot allow the user to delete their account
    return false
  }

  return true
}
